import { z } from "zod";

const EvidenceOpaqueIdSchema = z.uuid();
const EvidenceRequestIdSchema = z.string().regex(/^req_[0-9a-f-]{36}$/u);

export const EvidenceTypeSchema = z.enum(["photo", "note", "audio"]);
export const EvidenceStatusSchema = z.enum([
  "reserved",
  "uploading",
  "processing",
  "ready",
  "failed",
  "deleting",
  "deleted",
]);
export const EvidenceImageMimeSchema = z.enum(["image/jpeg", "image/png"]);

export const EvidenceSchema = z
  .object({
    id: EvidenceOpaqueIdSchema,
    workspace_id: EvidenceOpaqueIdSchema,
    task_id: EvidenceOpaqueIdSchema,
    assignment_id: EvidenceOpaqueIdSchema,
    child_id: EvidenceOpaqueIdSchema,
    type: EvidenceTypeSchema,
    status: EvidenceStatusSchema,
    note_text: z.string().min(1).max(2000).nullable(),
    mime_type: EvidenceImageMimeSchema.nullable(),
    byte_size: z
      .number()
      .int()
      .min(1)
      .max(5 * 1024 * 1024)
      .nullable(),
    width: z.number().int().min(1).max(12000).nullable(),
    height: z.number().int().min(1).max(12000).nullable(),
    processing_error_code: z.string().min(1).max(100).nullable(),
    created_by_actor_type: z.enum(["adult", "child"]),
    created_by_actor_id: EvidenceOpaqueIdSchema,
    ready_at: z.number().int().nullable(),
    deleted_at: z.number().int().nullable(),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
  })
  .strict();

export const EvidenceUploadSchema = z
  .object({
    id: EvidenceOpaqueIdSchema,
    evidence_id: EvidenceOpaqueIdSchema,
    declared_mime_type: EvidenceImageMimeSchema,
    declared_byte_size: z
      .number()
      .int()
      .min(1)
      .max(5 * 1024 * 1024),
    declared_width: z.number().int().min(1).max(12000),
    declared_height: z.number().int().min(1).max(12000),
    thumbnail_mime_type: EvidenceImageMimeSchema,
    thumbnail_byte_size: z
      .number()
      .int()
      .min(1)
      .max(512 * 1024),
    thumbnail_width: z.number().int().min(1).max(512),
    thumbnail_height: z.number().int().min(1).max(512),
    full_uploaded: z.boolean(),
    thumbnail_uploaded: z.boolean(),
    expires_at: z.number().int(),
    finalized_at: z.number().int().nullable(),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
  })
  .strict();

export const CreateEvidenceUploadRequestSchema = z
  .object({
    assignment_id: EvidenceOpaqueIdSchema,
    mime_type: EvidenceImageMimeSchema,
    byte_size: z
      .number()
      .int()
      .min(1)
      .max(5 * 1024 * 1024),
    width: z.number().int().min(1).max(12000),
    height: z.number().int().min(1).max(12000),
    thumbnail_mime_type: EvidenceImageMimeSchema,
    thumbnail_byte_size: z
      .number()
      .int()
      .min(1)
      .max(512 * 1024),
    thumbnail_width: z.number().int().min(1).max(512),
    thumbnail_height: z.number().int().min(1).max(512),
  })
  .strict()
  .refine((value) => value.width * value.height <= 12_000_000, {
    message: "image pixel count exceeds the allowed limit",
    path: ["width"],
  })
  .refine((value) => value.thumbnail_width * value.thumbnail_height <= 262_144, {
    message: "thumbnail pixel count exceeds the allowed limit",
    path: ["thumbnail_width"],
  })
  .refine(
    (value) => value.thumbnail_width <= value.width && value.thumbnail_height <= value.height,
    { message: "thumbnail dimensions cannot exceed the full image", path: ["thumbnail_width"] },
  )
  .refine((value) => value.thumbnail_mime_type === value.mime_type, {
    message: "thumbnail and full image must use the same sanitized format",
    path: ["thumbnail_mime_type"],
  });

export const EvidenceUploadReservationResponseSchema = z
  .object({
    evidence: EvidenceSchema,
    upload: EvidenceUploadSchema,
    upload_targets: z
      .object({
        full: z.string().min(1).max(500),
        thumbnail: z.string().min(1).max(500),
      })
      .strict(),
    idempotency_replayed: z.boolean(),
    request_id: EvidenceRequestIdSchema,
  })
  .strict();

export const EvidenceObjectUploadResponseSchema = z
  .object({
    upload: EvidenceUploadSchema,
    variant: z.enum(["full", "thumbnail"]),
    sha256: z.string().regex(/^[0-9a-f]{64}$/u),
    idempotency_replayed: z.boolean(),
    request_id: EvidenceRequestIdSchema,
  })
  .strict();

export const FinalizeEvidenceUploadRequestSchema = z
  .object({ expected_version: z.number().int().min(1) })
  .strict();

export const EvidenceMutationResponseSchema = z
  .object({
    evidence: EvidenceSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: EvidenceRequestIdSchema,
  })
  .strict();

export const CreateNoteEvidenceRequestSchema = z
  .object({ assignment_id: EvidenceOpaqueIdSchema, note: z.string().trim().min(1).max(2000) })
  .strict();

export const EvidenceListResponseSchema = z
  .object({ items: z.array(EvidenceSchema), request_id: EvidenceRequestIdSchema })
  .strict();

export const EvidenceAccessRequestSchema = z
  .object({ variant: z.enum(["full", "thumbnail"]).default("full") })
  .strict();

export const EvidenceAccessResponseSchema = z
  .object({
    grant_id: EvidenceOpaqueIdSchema,
    token: z.string().min(32).max(256),
    endpoint: z.string().min(1).max(500),
    expires_at: z.number().int(),
    request_id: EvidenceRequestIdSchema,
  })
  .strict();

export const DeleteEvidenceRequestSchema = z
  .object({ expected_version: z.number().int().min(1), reason: z.string().trim().min(1).max(500) })
  .strict();

export type Evidence = z.infer<typeof EvidenceSchema>;
export type EvidenceUpload = z.infer<typeof EvidenceUploadSchema>;
export type CreateEvidenceUploadRequest = z.infer<typeof CreateEvidenceUploadRequestSchema>;
export type CreateNoteEvidenceRequest = z.infer<typeof CreateNoteEvidenceRequestSchema>;
